import { AxiosError } from 'axios'
import { DateTime } from 'luxon'
import VuetifyColors from 'vuetify/lib/util/colors'

import { configSetup } from '@/config-setup'

let config: any

(async () => {
  config = await configSetup()
})()

type ErrorResponse = {
  code: string;
  message: string;
}

type SentryErrorResponse = {
  code: 'SENTRY';
  message: null;
}

/**
 * Consumes an error and returns it in a standardised format
 */
export function consumeError(error: AxiosError<ErrorResponse>): ErrorResponse | SentryErrorResponse {
  if (error.isAxiosError) {
    return consumeAxiosError(error)
  } else {
    return {
      code: 'SENTRY',
      message: null
    }
  }
}

const ERROR_RESPONSE_CODES = [400, 404, 409, 422]
const AUTH_ERROR_RESPONSE_CODES = [401, 403]

/**
 * Consumes an axios error and returns it in a standardised format
 */
function consumeAxiosError(error: AxiosError<ErrorResponse>): ErrorResponse {
  if (!error.response) {
    throw error
  }

  if (ERROR_RESPONSE_CODES.includes(error.response.status)) {
    return formatInvalidRequestError(error)
  } else if (AUTH_ERROR_RESPONSE_CODES.includes(error.response.status)) {
    return formatAuthError(error)
  } else if (error.response.status === 500) {
    return {
      code: 'SERVER_ERROR',
      message: 'An error occurred fulfilling your request'
    }
  }

  return {
    code: 'UNKNOWN_ERROR',
    message: 'An error occurred fulfilling your request'
  }
}

function formatInvalidRequestError(error: AxiosError<ErrorResponse>): ErrorResponse {
  if (!error.response) {
    throw error
  }

  if (error.response.status === 404) {
    return {
      code: 'NOT_FOUND',
      message: 'Entity not found'
    }
  } else {
    return {
      code: error.response.data.code,
      message: error.response.data.message
    }
  }
}

function formatAuthError(error: AxiosError<ErrorResponse>): ErrorResponse {
  if (!error.response) {
    throw error
  }

  if (error.response.status === 401) {
    return {
      code: 'UNAUTHORIZED',
      message: 'Session expired, please login'
    }
  } else if (error.response.status === 403) {
    return {
      code: 'FORBIDDEN',
      message: 'Incorrect permissions'
    }
  } else {
    return {
      code: error.response.data.code,
      message: error.response.data.message
    }
  }
}

/**
 * Calculates the sum of values by converting them to cents first
 * @param {number[]} values Dollar values to sum
 * @returns The dollar value total
 */
export function centSum(values: number[]) {
  const centTotal = values.reduce((total, value) => {
    return total + (value * 100)
  }, 0)

  return Math.round(centTotal) / 100
}

/**
 * Calculates the product of values by converting them to cents first
 * @param {number[]} values Dollar values to multiply
 * @returns The dollar value total
 */
export function centMultiply(values: number[]) {
  const centTotal = values.reduce((total, value) => {
    return total * (value * 100)
  }, 1)
  return centTotal / 100 ** (values.length)
}

/**
 * Rounds a number to 2 decimal places
 * @param {number} amount Value being rounded
 */
export function roundTwoDecimalPlaces(amount: number) {
  return Math.round(amount * 100) / 100
}

const SET_COLORS = [
  VuetifyColors.red, VuetifyColors.pink, VuetifyColors.purple, VuetifyColors.deepPurple, VuetifyColors.indigo,
  VuetifyColors.blue, VuetifyColors.lightBlue, VuetifyColors.cyan, VuetifyColors.teal, VuetifyColors.green,
  VuetifyColors.lightGreen, VuetifyColors.lime, VuetifyColors.yellow, VuetifyColors.amber, VuetifyColors.orange,
  VuetifyColors.deepOrange, VuetifyColors.brown, VuetifyColors.blueGrey, VuetifyColors.grey
]
const VALID_COLOR_COUNT = SET_COLORS.length * 3

function getRandomColor() {
  const index = Math.floor(Math.random() * SET_COLORS.length)
  const subColorIndex = Math.floor(Math.random() * 3)
  const subColors = [SET_COLORS[index].lighten3, SET_COLORS[index].base, SET_COLORS[index].darken3]
  return subColors[subColorIndex];
}

export function getColors(count: number) {
  if (count > VALID_COLOR_COUNT) {
    // If they have too many options, fall back to random HEX color
    const hex = Math.floor(Math.random() * 16777215).toString(16)
    return `#${hex}`
  }

  if (count === SET_COLORS.length) {
    return SET_COLORS.map(i => i.base)
  }

  const colors = new Set<string>()
  while (colors.size < count) {
    colors.add(getRandomColor())
  }

  return Array.from(colors)
}

function getDateTime(value: string | number | Date) {
  if (value instanceof Date) {
    return DateTime.fromJSDate(value)
  } else if (typeof value === 'string') {
    return DateTime.fromISO(value)
  } else {
    return DateTime.fromMillis(value)
  }
}

/**
 * Converts a value into a nice date format
 * @param {string | number | Date} value value to be converted, accepts any valid `Date` format
 */
export function prettyDate(value: string | number | Date) {
  if (!value) { return '' }
  return getDateTime(value).toFormat(config.longDateFormat)
}

/**
 * Converts a value into a nice time format
 * @param {string | number | Date} value value to be converted, accepts any valid `Date` format
 */
export function prettyTime(value: string | number | Date) {
  if (!value) { return '' }
  return getDateTime(value).toFormat('hh\':\'mm a')
}

export function viewOnly(entity: any) {
  return !entity || !!entity.rowReadOnly
}
